import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
    Body1,
    Body2,
    Body4,
    H3,
    Icon,
    Subtitle2,
    ResponsiveModal,
    TaskOptionDropDown,
    OverFlowBody2,
    CollapsableButton,
    IconButton,
    DropDown,
} from 'ensightful-koala-ui';

import { InputWithBorder } from 'components/molecules/TextInput';
import Modal from 'components/molecules/Modal';
import { FilterCheckBox } from 'components/molecules/Button';
import useClickOutside from 'services/hooks/useClickOutside';

const GridContainer = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: ${(props) => (props.mobile ? 'auto' : '228px 1fr')};
    grid-template-rows: ${(props) => (props.mobile ? '50px auto 120px' : 'auto max-content')};
    width: 100%;
    height: 100%;
    background: #ffffff;
    border-radius: ${(props) => (props.mobile ? '0px' : '12px')};
    overflow: hidden;
    z-index: 2;
`;

const TeamListContainer = styled.div`
    display: ${(props) => (props.mobile ? 'none' : 'block')};
    width: 100%;
    height: 100%;
    border-right: 1px solid #e0e0e0;
    overflow-y: auto;
`;

const TopContainer = styled.div`
    width: 100%;
    position: relative;
`;

const TeamDetailContainer = styled.div`
    width: 100%;
    height: ${(props) => (props.mobile ? '100vh' : '100%')};
    overflow-y: auto;
    padding: ${(props) => (props.mobile ? '12px' : '32px')};

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const TeamListHeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 32px 24px 8px 32px;
`;

const AddButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #6347ff;
    border-radius: 50%;
    width: 20px;
    height: 20px;

    &:hover {
        cursor: pointer;
    }
`;

const StyledCollapsableButton = styled(CollapsableButton)`
    & > div:first-child {
        padding: 0;
        margin: 24px 0px 16px 0px;
    }
`;

const CollapsableContent = styled.div`
    height: calc(80vh - 400px);
    overflow: auto;
`;

const StudentListTable = styled.table`
    width: 100%;
    height: 100%;
    max-height: 52px;
    overflow-y: auto;

    & tbody tr:nth-child(odd) {
        background-color: #f5f5f5;
    }
`;

const StudentRow = styled.tr`
    border-top: 1px #e0e0e0 solid;
    border-bottom: 1px #e0e0e0 solid;
`;

const NameContainer = styled.td`
    width: 80%;
`;

const CheckboxContainer = styled.td`
    width: 10%;

    &:hover {
        cursor: pointer;
    }
`;

const TeamRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 8px 32px 8px 32px;
    background-color: ${(props) => (props.selected ? '#efedff' : 'white')};

    &:hover {
        background-color: #efedff;
        cursor: pointer;
    }
`;

const StudentListContainer = styled.div`
    width: 100%;
    margin-top: 12px;
`;

const MemberTag = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    height: fit-content;
    padding: 4px 12px 4px 8px;
    background-color: #f5f5f5;
    border-radius: 6px;
    gap: 12px;

    & svg:hover {
        cursor: pointer;
    }
`;

const MemberTagContainer = styled.div`
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 6px;
    width: 100%;
    height: 80px;
    font-size: 14px;
    padding: 8px 15px;
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-top: 6px;
    overflow-y: auto;

    &:focus {
        outline: none;
        border: 1px solid #4136c0;
    }

    &:hover {
        outline: none;
        border: 1px solid #4136c0;
    }
`;

const StudentListHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 8px;
    width: 100%;
    flex-wrap: wrap;
`;

const StyledModal = styled(ResponsiveModal)`
    & .ant-modal-body {
        padding: 0;
        height: 80vh;
    }

    & .ant-modal-content {
        height: 100%;
    }
`;

const StyledClose = styled(Icon)`
    position: absolute;
    right: 0;
    top: 4px;

    cursor: pointer;
`;

const StyledDropDown = styled(DropDown)`
    top: 24px;
    padding: 8px;
    width: fit-content;
`;

const StyledIcon = styled(Icon)`
    & path {
        fill: black;
    }
`;

const IconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`;

const RowContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 28px 16px 12px;
`;

const RenderStudentList = ({ studentList = [], teamList = [], currentMembers = [], handleCheckbox }) => {
    const isAttendingATeam = (id) => {
        return teamList.some((team) => {
            if (team.members.some((member) => member.id === id)) {
                return true;
            }
            return false;
        });
    };
    return (
        <StudentListTable>
            <tbody>
                {studentList.map((student, index) => (
                    <StudentRow key={student.id}>
                        <NameContainer>
                            <OverFlowBody2
                                maxWidth={'200px'}
                                style={{
                                    color: isAttendingATeam(student.id) ? 'rgba(33,33,33, 0.2)' : 'rgb(33,33,33)',
                                }}
                            >
                                {student.name || student.email} {isAttendingATeam(student.id) && '(already in a team)'}
                            </OverFlowBody2>
                        </NameContainer>
                        <CheckboxContainer>
                            <FilterCheckBox
                                selected={student.group}
                                onChange={() => handleCheckbox(student)}
                                disabled={isAttendingATeam(student.id)}
                            />
                        </CheckboxContainer>
                    </StudentRow>
                ))}
            </tbody>
        </StudentListTable>
    );
};

const TeamSetUpModal = ({
    isVisible = false,
    teamList = [],
    studentList = [],
    device = 'desktop',
    currentTeamData = {},
    handleCurrentTeamData,
    handleTeamClick,
    handleAddTeam,
    handleRemoveMember,
    handleRemoveTeam,
    onCancel,
}) => {
    const [searchStudent, setSearchStudent] = useState('');
    const dropDownRef = useRef(null);

    const renderMembers = () => {
        return currentTeamData?.members?.map((member) => {
            return (
                <MemberTag key={member.id}>
                    {member?.name || member?.display_name || member?.first_name || member?.email || 'No name'}{' '}
                    <Icon kind={'close'} size={10} onClick={() => handleRemoveMember(member.id)} />
                </MemberTag>
            );
        });
    };

    const renderTeamList = () => {
        return teamList.map((team, index) => {
            return (
                <TeamRow
                    key={team.id}
                    selected={team.id === currentTeamData?.id}
                    onClick={() => handleTeamClick(team.id)}
                >
                    <Body1> {team.name || 'Team ' + (index + 1)}</Body1>
                    <Body4>
                        Members ({team.members?.length || 0}) - #id {team.id}
                    </Body4>
                </TeamRow>
            );
        });
    };

    const handleRemove = (id) => {
        return handleRemoveTeam(id);
    };

    return (
        <StyledModal
            closable={true}
            isOpen={isVisible}
            onCancel={onCancel}
            device={device}
            width={device === 'mobile' ? '100%' : '50%'}
            style={{ padding: 0 }}
        >
            <GridContainer mobile={device === 'mobile'}>
                <TeamListContainer mobile={device === 'mobile'}>
                    <TeamListHeaderContainer>
                        <Subtitle2>Teams</Subtitle2>
                        <IconButton active={true} kind={'plus'} onClick={handleAddTeam} />
                    </TeamListHeaderContainer>
                    {renderTeamList()}
                </TeamListContainer>
                <TeamDetailContainer mobile={device === 'mobile'}>
                    <TopContainer>
                        <H3> Edit Team Details</H3>
                        <TaskOptionDropDown
                            itemList={[
                                {
                                    title: (
                                        <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <Icon kind={'trashCan'} size={16} />
                                            Delete
                                        </span>
                                    ),
                                    value: 'delete',
                                },
                            ]}
                            onSelect={() => handleRemove(currentTeamData.id)}
                            style={{
                                top: 0,
                                right: 32,
                                position: 'absolute',
                            }}
                        />
                        {device === 'mobile' && <StyledClose kind={'close'} onClick={onCancel} size={14} />}
                    </TopContainer>

                    <Body4>#{currentTeamData.id} </Body4>
                    <InputWithBorder
                        error={false}
                        errorMessage=''
                        placeholder=''
                        required={false}
                        title='Team Name'
                        value={currentTeamData.name}
                        onChange={(value) => handleCurrentTeamData({ ...currentTeamData, name: value })}
                    />
                    <Body2 style={{ marginTop: 14 }}> Team Members ({currentTeamData?.members?.length})</Body2>
                    <MemberTagContainer>{renderMembers()}</MemberTagContainer>

                    <StudentListContainer>
                        <StyledCollapsableButton
                            defaultOpen
                            text={
                                <Body2 style={{ color: 'black' }}>Student Names (Total of {studentList.length}) </Body2>
                            }
                        >
                            <CollapsableContent>
                                <InputWithBorder
                                    error={false}
                                    errorMessage=''
                                    placeholder='Search'
                                    required={false}
                                    title=''
                                    width={'100%'}
                                    value={searchStudent}
                                    onChange={(value) => setSearchStudent(value)}
                                />
                                <RenderStudentList
                                    studentList={studentList.filter(
                                        (student) =>
                                            (
                                                student?.name ||
                                                student?.display_name ||
                                                student?.email ||
                                                student?.first_name ||
                                                'No name'
                                            )
                                                .toLowerCase()
                                                .includes(searchStudent.toLowerCase()) || searchStudent === ''
                                    )}
                                    teamList={teamList}
                                    currentMembers={currentTeamData?.members}
                                    handleCheckbox={(newMember) =>
                                        handleCurrentTeamData({
                                            ...currentTeamData,
                                            members: currentTeamData?.members?.some(
                                                (member) => member.id === newMember.id
                                            )
                                                ? currentTeamData?.members?.filter(
                                                      (member) => member.id !== newMember.id
                                                  )
                                                : [
                                                      ...currentTeamData?.members,
                                                      { ...newMember, group: currentTeamData?.number },
                                                  ],
                                        })
                                    }
                                />
                            </CollapsableContent>
                        </StyledCollapsableButton>
                    </StudentListContainer>
                </TeamDetailContainer>
            </GridContainer>
        </StyledModal>
    );
};

export default TeamSetUpModal;
